import type { WorkBook } from "xlsx";
import { SYSTEM_ANALYSIS_WORKSHEETS } from "@/domain/systemAnalysis";
import type { SourceRow } from "@/domain/types";

/**
 * Workbooks deployed next to the app (in web/public/); selectable with
 * `?data=<name>`. None are deployed at the moment, see docs/todo.md.
 */
const ALLOWED_DATA_FILES = ["Tiltaksliste", "RePS", "Konsolidert", "CHAMPS_pilot"] as const;
const DEFAULT_DATA_FILE = "Tiltaksliste";

export interface ParsedWorkbook {
  /** Rows of the first worksheet (the intervention list). */
  interventionRows: SourceRow[];
  /** Annex 4 sheets, or `null` when the workbook has neither. */
  systemAnalysis: { criterionRows: SourceRow[]; dataRows: SourceRow[] } | null;
}

export class IncompleteSystemAnalysisError extends Error {
  constructor() {
    super("Systemanalyse requires both Annex4_Criterion and Annex4_Data worksheets.");
  }
}

type Xlsx = typeof import("xlsx");

function sheetRows(
  xlsx: Xlsx,
  workbook: WorkBook,
  name: string | undefined,
  defval?: string,
): SourceRow[] {
  const sheet = name ? workbook.Sheets[name] : undefined;
  return sheet
    ? xlsx.utils.sheet_to_json<SourceRow>(sheet, defval === undefined ? {} : { defval })
    : [];
}

/** Parses a workbook. SheetJS is loaded on demand to keep it out of the main bundle. */
export async function parseWorkbook(data: ArrayBuffer): Promise<ParsedWorkbook> {
  const xlsx = await import("xlsx");
  const workbook = xlsx.read(data, { type: "array" });
  const hasCriterion = !!workbook.Sheets[SYSTEM_ANALYSIS_WORKSHEETS.criterion];
  const hasData = !!workbook.Sheets[SYSTEM_ANALYSIS_WORKSHEETS.data];
  if (hasCriterion !== hasData) throw new IncompleteSystemAnalysisError();
  return {
    interventionRows: sheetRows(xlsx, workbook, workbook.SheetNames[0]),
    systemAnalysis: hasCriterion
      ? {
          criterionRows: sheetRows(xlsx, workbook, SYSTEM_ANALYSIS_WORKSHEETS.criterion, ""),
          dataRows: sheetRows(xlsx, workbook, SYSTEM_ANALYSIS_WORKSHEETS.data, ""),
        }
      : null,
  };
}

/** Resolves the `?data=` parameter to an allowed workbook base name. */
export function resolveDataFile(param: string | null): string {
  const name = (param ?? "").replace(/\.[^.]*$/, "");
  return (ALLOWED_DATA_FILES as readonly string[]).includes(name) ? name : DEFAULT_DATA_FILE;
}

/** Fetches the deployed workbook (`.xlsx`, then `.xls`). Returns `null` if neither exists. */
export async function fetchDeployedWorkbook(
  baseName: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ParsedWorkbook | null> {
  for (const extension of ["xlsx", "xls"]) {
    const response = await fetchImpl(`/${baseName}.${extension}`);
    if (response.ok) return await parseWorkbook(await response.arrayBuffer());
  }
  return null;
}
